import {
  AlertTrigger,
  PairedCostRouteItem,
  PairedCostRouteItemWithQuery,
  schAlertTrigger,
} from '@aloft/toolkit-shared';

import { OrderState } from '../../../../redux/types.ts';

type FixRoute = PairedCostRouteItem | PairedCostRouteItemWithQuery;

const INSPECTIFY_WORKFLOW: PairedCostRouteItem[] = [
  'features',
  'market',
  'cost-approach',
  'adjustments-grid',
  'appraisify-commentary',
  'reconciliation',
];

const ROUTE_TO_NAV_TRIGGER: Record<PairedCostRouteItem, AlertTrigger> = {
  details: 'navigate/sales-comparables',
  features: 'navigate/sales-comparables',
  market: 'navigate/market',
  analysis: 'navigate/cost-approach',
  'cost-approach': 'navigate/cost-approach',
  adjustments: 'navigate/adjustments',
  'adjustments-grid': 'navigate/adjustments',
  commentary: 'navigate/commentary',
  'appraisify-commentary': 'navigate/commentary',
  'craftsman-cost-approach': 'navigate/cost-approach',
  'rental-features': 'navigate/rental-comparables',
  reconciliation: 'navigate/reconciliation',
};

const normalizeAlertFixRoute = (fixRoute: FixRoute): PairedCostRouteItem => {
  const base = fixRoute.split('?')[0]?.split('#')[0] ?? fixRoute;
  if (base === 'adjustments-grid') return 'adjustments-grid';
  return base as PairedCostRouteItem;
};

const resolveWorkflowRoute = (fixRoute: PairedCostRouteItem): PairedCostRouteItem => {
  if (fixRoute === 'analysis') return 'cost-approach';
  if (fixRoute === 'adjustments') return 'adjustments-grid';
  if (fixRoute === 'commentary') return 'appraisify-commentary';
  return fixRoute;
};

const getNavTriggerForRoute = (route: PairedCostRouteItem): AlertTrigger => {
  const workflowRoute = resolveWorkflowRoute(route);
  return ROUTE_TO_NAV_TRIGGER[workflowRoute];
};

export const getAlertTriggers = ({
  fixRoute,
  order,
  includeUpdate = false,
}: {
  fixRoute: FixRoute;
  order: OrderState;
  includeUpdate?: boolean;
}): AlertTrigger[] => {
  if (!order.isInspectifyOrder) return [];

  const normalized = normalizeAlertFixRoute(fixRoute);
  const workflowRoute = resolveWorkflowRoute(normalized);
  const fixIndex = INSPECTIFY_WORKFLOW.indexOf(workflowRoute);

  const triggers = new Set<AlertTrigger>();

  if (fixIndex >= 0) {
    for (let i = fixIndex + 1; i < INSPECTIFY_WORKFLOW.length; i++) {
      const downstreamRoute = INSPECTIFY_WORKFLOW[i];
      if (downstreamRoute) {
        triggers.add(getNavTriggerForRoute(downstreamRoute));
      }
    }
  } else {
    triggers.add(getNavTriggerForRoute(normalized));
  }

  if (includeUpdate) {
    switch (normalized) {
      case 'adjustments':
        triggers.add('update/adjustments' as AlertTrigger);
        break;
      case 'features':
        triggers.add('update/comparables' as AlertTrigger);
        break;
      default:
        break;
    }
    triggers.add(schAlertTrigger.enum['update/adjustments']);
  }

  return [...triggers];
};
